import { useState } from "react";

import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { Box, IconButton, ToggleButton as MUIToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { getMaskedValue } from "components/elements/MaskedModeWrapper";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import styled, { createGlobalStyle } from "styled-components";
import { IInvestmentChart } from "types/types";
import { getColorByMode } from "utils";
import { renderPrice } from "utils/formatter";

import ChartModal from "./ChartModal";

dayjs.extend(isToday);

const GlobalStyle = createGlobalStyle`
 .chart .apexcharts-legend-series[rel="1"] {
    display: none !important;
  }
`;

const ToggleButton = styled(MUIToggleButton)`
  font-size: 0.8rem;
  padding: 4px 10px;
`;

interface Props {
  chartData: IInvestmentChart;
  baseCurrency: string;
  counterCurrency: string;
  baseCurrDisplayedScale: number;
  counterCurrDisplayedScale: number;
  counterCurrShortDisplayedScale: number;
  targetValuePrice?: number;
}

interface ChartControlsProps {
  logarithmic: boolean;
  onLogarithmicChange: () => void;
  showFullscreen?: boolean;
  onFullscreenChange?: () => void;
}

const ChartControls = ({
  logarithmic,
  onLogarithmicChange,
  showFullscreen = false,
  onFullscreenChange,
}: ChartControlsProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
      <Box sx={{ flex: 1 }} />
      <ToggleButtonGroup color="primary" value={logarithmic} onChange={onLogarithmicChange} exclusive>
        <ToggleButton value={false}>{t("scale.linear")}</ToggleButton>
        <ToggleButton value={true}>{t("scale.log")}</ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
        {showFullscreen && (
          <IconButton size="small" onClick={onFullscreenChange}>
            <FullscreenIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const Chart = ({
  chartData,
  baseCurrency,
  counterCurrency,
  baseCurrDisplayedScale,
  counterCurrDisplayedScale,
  counterCurrShortDisplayedScale,
  targetValuePrice,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMasked } = useUserDataContext();
  const { colors } = useMUIThemeModeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [logarithmic, setLogarithmic] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  const labels = chartData.points.map((item) =>
    Intl.DateTimeFormat("cs-CZ").format(new Date(dayjs(item.d).format()))
  );
  const min = chartData.minValue;

  const series = [
    {
      name: t("amountAccumulated"),
      data: chartData.points.map((item) => item.a),
    },
    {
      name: t("invested"),
      data: chartData.points.map((item) => item.i),
    },
    {
      name: t("value"),
      data: chartData.points.map((item) => item.v),
    },
  ];
  const strokeCurve: ApexStroke["curve"] = ["stepline", "stepline", "smooth"];
  const strokeWidth = [0, 3, 3];
  const chartColors = ["transparent", getColorByMode(theme, theme.palette.grey[300], colors.black), theme.palette.primary.main];

  if (targetValuePrice) {
    series.push({
      name: t("targetValue"),
      data: chartData.points.map((item) => item.t),
    });
    strokeCurve.push("smooth");
    strokeWidth.push(3);
    chartColors.push(theme.palette.info.main);
  }

  // chart config
  const options: ApexOptions = {
    theme: { mode: theme.palette.mode },
    tooltip: {
      followCursor: true,
      inverseOrder: true,
      y: {
        formatter: (val: number, { seriesIndex }: any) => {
          // not possible to have a separate formatter for each data series, therefore this hack
          if (seriesIndex === 0) {
            return getMaskedValue(isMasked, renderPrice(val, baseCurrency, baseCurrDisplayedScale, true));
          } else {
            return getMaskedValue(isMasked, renderPrice(val, counterCurrency, counterCurrDisplayedScale));
          }
        },
      },
    },
    colors: chartColors,
    chart: {
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
      background: "transparent",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: strokeCurve,
      width: strokeWidth,
    },
    grid: {
      row: {
        colors: [colors.legacyGray100, "transparent"],
        opacity: 0.5,
      },
    },
    xaxis: {
      categories: labels,
      labels: {
        hideOverlappingLabels: true,
        rotate: -30,
        show: true,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      show: true,
      labels: {
        formatter: (val: any) => {
          return getMaskedValue(isMasked, renderPrice(val, counterCurrency, counterCurrShortDisplayedScale));
        },
        minWidth: logarithmic ? 0 : 40,
      },
      logarithmic,
      min,
      forceNiceScale: logarithmic, // ignore min setting on the logarithmic scale due to apexcharts issue
    },
  };

  return (
    <>
      <Box sx={{ pt: { xs: 2 }, pr: { xs: 0, md: 2 }, textAlign: "center" }}>
        <GlobalStyle />
        <ChartControls
          logarithmic={logarithmic}
          onLogarithmicChange={() => setLogarithmic(!logarithmic)}
          showFullscreen={!isMobile}
          onFullscreenChange={() => setFullScreen(!fullScreen)}
        />
        <ReactApexChart className="chart" options={options} series={series} type="line" width={"100%"} height={400} />
      </Box>
      {fullScreen && (
        <ChartModal title={t("chartHistory.title")} handleClose={() => setFullScreen(false)}>
          <Box sx={{ textAlign: "center", height: "100%", display: "flex", flexDirection: "column" }}>
            <ChartControls logarithmic={logarithmic} onLogarithmicChange={() => setLogarithmic(!logarithmic)} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ReactApexChart
                className="chart"
                options={options}
                series={series}
                type="line"
                width={"100%"}
                height={"95%"}
              />
            </Box>
          </Box>
        </ChartModal>
      )}
    </>
  );
};

export default Chart;
